import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AxiosResponse } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';

import { Input } from '@/common';
import { Button, Label, LABEL_TYPE, Spinner, Textarea } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { TEXT } from '@/constants';
import { AuthContext } from '@/contexts';
import { AssetScope, AssetType, PERMISSION_TYPE } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { createEditTextAssetSchema } from '@/pages/Event/Text/Validation';
import {
  useCreateAssetMutation,
  useEstimateReadingTime,
  useGenerateSpeechText,
  useGetAssetQuery,
  useUpdateAsset
} from '@/services';
import {
  AddATextAssetValues,
  CreateEditAssetPayload,
  CreateTextAssetProps,
  GeneratedText,
  GenerateSpeechTextPayload,
  GenerateSpeechTextResponse
} from '@/types';

import { GenerateSpeechTextModal } from './GenerateSpeechTextModal';

export const CreateEdit = ({
  id,
  recommendedAsset,
  handleClose,
  textId,
  disabled,
  maxAssets
}: CreateTextAssetProps) => {
  const [t] = useTranslation('common');
  const hasRecommendedText = !!recommendedAsset;
  const isEditPage = !!textId;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isGenerateTextModalOpen, setIsGenerateTextModalOpen] = useState<boolean>(false);
  const [isGeneratingText, setIsGeneratingText] = useState<boolean>(false);
  const [generatedText, setGeneratedText] = useState<GeneratedText | null>(null);
  const { permissions } = useContext(AuthContext);
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const { mutateAsync: createAsset } = useCreateAssetMutation(queryClient, {
    onSuccess: (response?: AxiosResponse) => {
      handleClose();
      setIsSubmitting(false);
      showToast(
        ToastType.SUCCESS,
        t('assetSuccessfullyCreated', { assetName: response?.data.assetName })
      );
    },
    onError: () => {
      setIsSubmitting(false);
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const { mutateAsync: updateAsset } = useUpdateAsset(queryClient, {
    onSuccess: () => {
      handleClose();
      setIsSubmitting(false);
      showToast(ToastType.SUCCESS, t('assetSuccessfullyUpdated'));
    },
    onError: () => {
      setIsSubmitting(false);
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const { data, isLoading } = useGetAssetQuery(Number(textId));

  const { mutate: generateSpeechText } = useGenerateSpeechText({
    onSuccess: (response?: AxiosResponse<GenerateSpeechTextResponse>) => {
      setIsGeneratingText(false);
      setIsGenerateTextModalOpen(false);
      showToast(ToastType.SUCCESS, t('speechTextGeneratedSuccessfully'));

      if (!response?.data) {
        return;
      }

      setGeneratedText({
        title: response.data.generatedTitle,
        description: response.data.generatedText
      });
    },
    onError: (error) => {
      setIsGeneratingText(false);
      showToast(
        ToastType.ERROR,
        error.response?.data?.ErrorMessages?.[0]?.DisplayMessage || t('speechTextGenerationError')
      );
    }
  });
  const { mutateAsync: estimateReadingTime } = useEstimateReadingTime();

  const submitFormAsTextFile = async (
    values: AddATextAssetValues,
    { resetForm }: FormikHelpers<AddATextAssetValues>
  ) => {
    setIsSubmitting(true);
    const readingTime = await estimateReadingTime(values.description);
    const asset: CreateEditAssetPayload = {
      id: Number(values.id),
      eventId: Number(id),
      assetType: AssetType.TEXT,
      assetScope: AssetScope.PRIVATE,
      assetName: values.title,
      assetUrl: '',
      assetSize: 0,
      thumbnailUrl: '',
      additionalData: values.description,
      duration: readingTime.data,
      ...(!hasOrgAccess && {
        tags: [
          {
            name: TEXT
          }
        ]
      })
    };

    if (isEditPage) {
      await updateAsset(asset);
    } else {
      await createAsset(asset);
    }

    if (generatedText) {
      setGeneratedText(null);
    }
    resetForm({
      values: {
        id: '',
        title: '',
        description: ''
      }
    });
  };

  const getInitialValues = () => {
    return {
      id: data?.data?.id,
      title:
        data?.data?.assetName ??
        generatedText?.title ??
        (hasRecommendedText ? recommendedAsset?.assetName : ''),
      description:
        data?.data?.additionalData ??
        generatedText?.description ??
        (hasRecommendedText ? recommendedAsset?.additionalData : '')
    };
  };

  const handleOpenGenerateTextModal = () => {
    setIsGenerateTextModalOpen(true);
  };

  const handleCloseGenerateTextModal = () => {
    setIsGenerateTextModalOpen(false);
  };

  const handleGenerateText = (values: GenerateSpeechTextPayload) => {
    setIsGeneratingText(true);
    generateSpeechText(values);
  };

  return (
    <div className='flex w-full flex-col md:w-1/2'>
      {isEditPage && isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className='mb-2 hidden border-b border-secondary-blue-gray pb-2 md:block'>
            <div className='flex w-full items-center justify-between'>
              <Label text={t(isEditPage ? 'editText' : 'writeText')} type={LABEL_TYPE.H2} />
            </div>
          </div>
          <Label
            type={LABEL_TYPE.PARAGRAPH2}
            text={t('pleaseAddOrWriteYourSpeech', { x: maxAssets })}
            className='pb-4 font-sans'
          />
          <Formik
            enableReinitialize={true}
            initialValues={getInitialValues()}
            onSubmit={submitFormAsTextFile}
            validationSchema={createEditTextAssetSchema}>
            {({ errors, touched, dirty }) => (
              <Form className='flex h-full flex-col justify-between gap-4 pb-6'>
                <div className='flex h-full flex-col gap-4'>
                  <Input
                    id='title'
                    label={t('title')}
                    type='text'
                    isRequired
                    width='w-full '
                    errorMessage={errors.title as string}
                    isInvalid={!!(errors.title && touched.title)}
                    disabled={disabled}
                  />
                  <Textarea
                    label={t('description')}
                    id='description'
                    isRequired
                    errorMessage={errors.description as string}
                    isInvalid={!!(errors.description && touched.description)}
                    className='h-full max-h-64 min-h-28'
                    containerClassName='h-full'
                    disabled={disabled}
                  />
                </div>
                <div className='flex w-full justify-between gap-x-2'>
                  {!isEditPage && (
                    <Button
                      variant={ButtonVariant.SECONDARY}
                      handleOnClick={handleOpenGenerateTextModal}>
                      {t('generateSpeechText')}
                    </Button>
                  )}
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    type='submit'
                    disabled={isSubmitting || (isEditPage && !dirty) || disabled}>
                    {t('save')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </>
      )}
      <GenerateSpeechTextModal
        isOpen={isGenerateTextModalOpen}
        onClose={handleCloseGenerateTextModal}
        onGenerate={handleGenerateText}
        eventId={Number(id)}
        isGenerating={isGeneratingText}
      />
    </div>
  );
};
